import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { INotification } from "models/notification/notification.model";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "services/api/notifications";
import { AppThunk, RootState } from "store";

interface INotificationsSlice {
  loadingStatus: ApiLoadingStatus;
  notifications: INotification[];
  unreadCount: number;
  detailNotificationId: number | null;
}

export const NotificationsSliceInitialState: INotificationsSlice = {
  loadingStatus: ApiLoadingStatus.IDLE,
  notifications: [],
  unreadCount: 0,
  detailNotificationId: null,
};

export const notificationsSlice = createSlice({
  name: "notificationsSlice",
  initialState: NotificationsSliceInitialState,
  reducers: {
    setData: (
      state,
      action: PayloadAction<{
        notifications: INotification[];
        unreadCount: number;
      }>
    ) => {
      state.notifications = action.payload.notifications;
      state.unreadCount = action.payload.unreadCount;
      state.loadingStatus = ApiLoadingStatus.SUCCESS;
    },

    setStatus: (state, action: PayloadAction<ApiLoadingStatus>) => {
      state.loadingStatus = action.payload;
    },

    markReadLocally: (state, action: PayloadAction<number>) => {
      const notification = state.notifications.find(
        (item) => item.id === action.payload
      );
      if (notification && !notification.read) {
        notification.read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    markAllReadLocally: (state) => {
      state.notifications.forEach((notification) => {
        notification.read = true;
      });
      state.unreadCount = 0;
    },

    // Clears the badge count only — leaves each item's `read` flag intact so
    // unread items keep their highlight for the current viewing.
    clearUnreadCount: (state) => {
      state.unreadCount = 0;
    },

    openNotificationDetail: (state, action: PayloadAction<number>) => {
      state.detailNotificationId = action.payload;
    },
    closeNotificationDetail: (state) => {
      state.detailNotificationId = null;
    },
  },
});

export const {
  setData,
  setStatus,
  markReadLocally,
  markAllReadLocally,
  clearUnreadCount,
  openNotificationDetail,
  closeNotificationDetail,
} = notificationsSlice.actions;

export const fetchNotificationsThunk = (): AppThunk => {
  return async (dispatch) => {
    await dispatch(setStatus(ApiLoadingStatus.LOADING));

    const { data, error } = await fetchNotifications();

    if (data && !error) {
      dispatch(
        setData({
          notifications: data.notifications ?? [],
          unreadCount: data.unreadCount ?? 0,
        })
      );
    } else {
      dispatch(setStatus(ApiLoadingStatus.FAILED));
    }
  };
};

export const markNotificationReadThunk = (id: number): AppThunk => {
  return async (dispatch) => {
    // Optimistic update; the receipt is persisted in the background.
    dispatch(markReadLocally(id));
    await markNotificationRead(id);
  };
};

export const markAllNotificationsReadThunk = (): AppThunk => {
  return async (dispatch) => {
    dispatch(markAllReadLocally());
    await markAllNotificationsRead();
  };
};

// Opening the bell marks everything read: refresh, clear the badge, and persist
// read on the server. Items keep their unread highlight for this view; they
// come back read on the next open.
export const openNotificationsThunk = (): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(fetchNotificationsThunk());
    const { unreadCount } = (getState() as RootState).notificationsSlice;
    if (unreadCount > 0) {
      dispatch(clearUnreadCount());
      await markAllNotificationsRead();
    }
  };
};
